import { Grid, NumberInput, Skeleton, Stack } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';

import type {
  EnvironmentalCalculations,
  EnvironmentalDataFormData,
} from '../../../types/reports.js';
import { FormSubsection } from '../../ui/FormSubsection.js';
import { LabelWithInfo } from '../../ui/LabelWithInfo.js';
import { DECIMAL_INPUT_PROPS } from '../../form/numberFieldPresets.js';
import { ValidatedNumberInput } from '../../form/validatedFields.js';
import { PreviousValueForField } from '../../form/PreviousValueLine.js';
import { RegisterFieldTab } from '../../form/RegisterFieldTab.js';

interface WasserSectionProps {
  form: UseFormReturnType<EnvironmentalDataFormData>;
  waterCalc: EnvironmentalCalculations | null;
  disabled?: boolean;
  loading?: boolean;
}

export function WasserSection({
  form,
  waterCalc,
  disabled,
  loading,
}: WasserSectionProps) {
  // waterConsumption is NEVER written back via form.setFieldValue — the backend
  // is authoritative (read-only field). The live preview itself (calculate
  // endpoint call + debounce) lives in the parent EnvironmentalDataForm, fed by
  // the single onValuesChange source (Pitfall 4 — no second data-driven effect
  // hook here).
  const consumptionValue =
    waterCalc?.waterConsumption != null && waterCalc.waterConsumption !== ''
      ? parseFloat(waterCalc.waterConsumption)
      : '';

  if (loading) {
    return (
      <Grid>
        {[0, 1, 2, 3].map((i) => (
          <Grid.Col span={{ base: 12, md: 6 }} key={i}>
            <Skeleton height={60} />
          </Grid.Col>
        ))}
      </Grid>
    );
  }

  return (
    <FormSubsection title="Wasser">
      <Stack gap="md">
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <ValidatedNumberInput
              form={form}
              field="waterWithdrawal"
              kind="decimal"
              fieldLabel="Wasserentnahme"
              label={
                <LabelWithInfo
                  label="Wasserentnahme"
                  info="Süßwasserquellen (Oberflächenwasser, Grundwasser, Steinbruchwasser, Kommunal-/Trinkwasser, externes Abwasser), gesammeltes Regenwasser aus der Produktion sowie Salz- und Brackgewässer. Quelle: GCCA Key Performance Indicators."
                />
              }
              unit="m³"
              disabled={disabled}
              {...DECIMAL_INPUT_PROPS}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <ValidatedNumberInput
              form={form}
              field="waterDischarge"
              kind="decimal"
              fieldLabel="Wasserableitung / Abwasser"
              label={
                <LabelWithInfo
                  label="Wasserableitung / Abwasser"
                  info="Süß- und Salzwasserabfluss in Meere, Oberflächengewässer, Untergrund/Brunnen, externe Wasseraufbereitung, Verwendung durch andere Nutzer sowie nicht genutzte Wasserressourcen. Quelle: GCCA Key Performance Indicators."
                />
              }
              unit="m³"
              disabled={disabled}
              {...DECIMAL_INPUT_PROPS}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <ValidatedNumberInput
              form={form}
              field="waterRecycled"
              kind="decimal"
              label="Zurückgewonnenes und wiederverwendetes Wasser"
              unit="m³"
              disabled={disabled}
              {...DECIMAL_INPUT_PROPS}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <NumberInput
              label={
                <LabelWithInfo
                  label="Wasserverbrauch (m³)"
                  info="Automatisch berechnet: Wasserentnahme − Wasserableitung. Schreibgeschützt."
                />
              }
              value={consumptionValue}
              readOnly
              disabled
              suffix=" m³"
              {...DECIMAL_INPUT_PROPS}
            />
            <RegisterFieldTab field="waterConsumption" label="Wasserverbrauch" />
            <PreviousValueForField field="waterConsumption" />
          </Grid.Col>
        </Grid>
      </Stack>
    </FormSubsection>
  );
}
